import { AxiosError } from 'axios'

import { createAgnosticHttpClient } from 'auth/infra/createAgnosticHttpClient'

import { type ShipmentCharge } from '../domain/ShipmentCharges'
import { mapApiToShipmentCharge } from '../infra/ShipmentChargeMapper'
import { ShipmentChargesRepository } from '../infra/ShipmentChargesRepository'

export class CancelShipmentChargeError extends Error {
  updatedCharge?: ShipmentCharge

  constructor(message?: string, options?: ErrorOptions, updatedCharge?: ShipmentCharge) {
    super(message, options)
    this.updatedCharge = updatedCharge
    this.name = 'CancelShipmentChargeError'
  }
}

export type CancelShipmentChargeFactoryDependencies = {
  repository: ShipmentChargesRepository
}

export function cancelShipmentChargeFactory({
  repository,
}: CancelShipmentChargeFactoryDependencies) {
  return async function patch(
    shipmentUUID: string,
    chargeUUID: string,
    cancelReason: string,
    config?: { signal?: AbortSignal }
  ) {
    try {
      return await repository.cancelShipmentCharge(shipmentUUID, chargeUUID, cancelReason, config)
    } catch (error) {
      if (error instanceof AxiosError) {
        // When having an error because of outdated charge data, we receive a
        // shipment charge object in the response, which needs to be used to update
        // the UI with the up-to-date information
        const { response } = error
        const charge = response?.data?.shipment_charge
        const parsedCharge = charge ? mapApiToShipmentCharge(charge) : undefined
        const message = response?.data?.error

        throw new CancelShipmentChargeError(message, { cause: error }, parsedCharge)
      }

      throw new Error('Charge could not be canceled. Please try again.', { cause: error })
    }
  }
}

export function cancelShipmentCharge(
  shipmentUUID: string,
  chargeUUID: string,
  cancelReason: string,
  config?: { signal?: AbortSignal }
) {
  const httpClient = createAgnosticHttpClient()
  const repository = new ShipmentChargesRepository({ httpClient })
  const patch = cancelShipmentChargeFactory({ repository })

  return patch(shipmentUUID, chargeUUID, cancelReason, config)
}
